import type { MyProfile } from '@pm/schemas';
import { displayPhone, formatPhone } from '@pm/schemas';
import { Button, ErrorState, Field, Input, LabelCaps, PageHeader, SkeletonRows } from '@pm/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { AvatarUploader } from '@/components/AvatarUploader';
import { api, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AccessTokensCard } from './AccessTokensCard';
import { useToast } from '@/lib/toast';

const PROFILE_KEY = ['my-profile'] as const;

/** Meu perfil: o próprio usuário altera foto, telefone e senha. Nome e e-mail ficam com o admin. */
export function ProfilePage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: PROFILE_KEY,
    queryFn: () => api.get<MyProfile>('/me/profile'),
  });

  return (
    <div className="flex max-w-[900px] flex-col gap-gap-lg">
      <PageHeader
        title="Meu perfil"
        subtitle="Foto, telefone, senha e tokens de acesso para agentes. Vale só para você."
      />
      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : isError || !data ? (
        <ErrorState message={error?.message} onRetry={() => void refetch()} />
      ) : (
        <>
          <div className="grid items-start gap-gap-lg lg:grid-cols-2">
            <ProfileCard profile={data} />
            <PasswordCard />
          </div>
          <AccessTokensCard />
        </>
      )}
    </div>
  );
}

function ProfileCard({ profile }: { profile: MyProfile }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { refetchMe } = useAuth();
  const [phone, setPhone] = useState(displayPhone(profile.phone));
  const [saving, setSaving] = useState(false);
  const dirty = phone !== displayPhone(profile.phone);

  // A foto aparece na sidebar e no topo: recarrega o "me" além do perfil.
  const afterChange = async (next?: MyProfile) => {
    if (next) qc.setQueryData(PROFILE_KEY, next);
    else await qc.invalidateQueries({ queryKey: PROFILE_KEY });
    await refetchMe();
  };

  return (
    <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4">
      <LabelCaps>Dados</LabelCaps>
      <AvatarUploader
        name={profile.name}
        url={profile.avatar_url}
        onUpload={async (body) => afterChange(await api.put<MyProfile>('/me/avatar', body))}
        onRemove={async () => {
          await api.delete('/me/avatar');
          await afterChange();
        }}
      />
      <Field label="Nome" htmlFor="pf-name">
        <Input id="pf-name" value={profile.name} disabled />
      </Field>
      <Field
        label="E-mail"
        htmlFor="pf-email"
        hint="Para mudar nome ou e-mail, fale com um administrador."
      >
        <Input id="pf-email" value={profile.email} mono disabled />
      </Field>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setSaving(true);
          api
            .patch<MyProfile>('/me/profile', { phone: phone.trim() || null })
            .then(async (next) => {
              await afterChange(next);
              setPhone(displayPhone(next.phone));
              toast.success('Telefone salvo');
            })
            .catch((err: unknown) => toast.error(err))
            .finally(() => setSaving(false));
        }}
      >
        <Field label="Telefone" htmlFor="pf-phone">
          <Input
            id="pf-phone"
            mono
            type="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            placeholder="(11) 98765-4321"
            maxLength={19}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          className="self-end"
          loading={saving}
          disabled={!dirty}
        >
          Salvar
        </Button>
      </form>
    </div>
  );
}

function PasswordCard() {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string }>({});
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!current) errs.current = 'Informe a senha atual.';
    if (next.length < 8) errs.next = 'A nova senha precisa ter ao menos 8 caracteres.';
    else if (next === current) errs.next = 'A nova senha precisa ser diferente da atual.';
    if (confirm !== next) errs.confirm = 'As senhas não conferem.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await api.put('/auth/password', { current_password: current, new_password: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      toast.success('Senha alterada', 'As outras sessões abertas foram encerradas.');
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'PASSWORD_CURRENT_INVALID')
        setErrors({ current: err.message });
      else toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      noValidate
      className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4"
    >
      <LabelCaps>Alterar senha</LabelCaps>
      <p className="text-sm text-text-3">
        Ao trocar a senha, as sessões abertas em outros navegadores são encerradas.
      </p>
      <Field label="Senha atual" htmlFor="pw-current" error={errors.current}>
        <Input
          id="pw-current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          invalid={Boolean(errors.current)}
        />
      </Field>
      <Field label="Nova senha" htmlFor="pw-next" error={errors.next} hint="Ao menos 8 caracteres.">
        <Input
          id="pw-next"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          invalid={Boolean(errors.next)}
        />
      </Field>
      <Field label="Confirmar nova senha" htmlFor="pw-confirm" error={errors.confirm}>
        <Input
          id="pw-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          invalid={Boolean(errors.confirm)}
        />
      </Field>
      <Button type="submit" variant="primary" className="self-end" loading={saving}>
        Alterar senha
      </Button>
    </form>
  );
}
